import React, { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import AutoComplete, { SuggestionInterface } from '@components/ui/AutoComplete';
import AppContext from '@context';
import { useCitiesSuggestions } from '@services/cities';
import { FuelIcon } from '@frete.com/fuel-react';
import { StateMap } from '@utils/StateMap';
import { JourneysStateInterface } from '@context/reducers/journeys/journeys.type';
import { JourneyMonitoringAddressDataInterface } from '@interface/journeys';
import { FieldCityPropsInterface } from './FieldCityTypes';
import { EditAddressType } from '../schema';
import * as Styled from './FieldCityStyles';

function FieldCity({ flow }: FieldCityPropsInterface) {
  const {
    control,
    clearErrors,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<EditAddressType>();
  const {
    state: { journeys },
  } = useContext(AppContext);
  const [valueCityState, setValueCityState] = useState('');
  const nameFieldCity = `${flow}.place.city.name` as const;
  const nameFieldStateName = `${flow}.place.state.name` as const;
  const nameFieldAcronymName = `${flow}.place.state.acronym` as const;
  const fieldCityValue = watch(nameFieldCity);
  const fieldStateAcronyValue = watch(nameFieldAcronymName);
  const { data: journeySelection } =
    journeys.selection as JourneysStateInterface['selection'];
  const currentPickup = journeySelection?.journey?.address?.pickup;
  const currentDelivery = journeySelection?.journey?.address?.delivery;
  let labelCityField = 'Cidade de coleta*';

  function getMessageCityError() {
    const errorCity = (errors?.[flow]?.place?.city?.name?.message ||
      '') as string;
    const errorState = (errors?.[flow]?.place?.state?.name?.message ||
      '') as string;
    const errorStateAcronym = (errors?.[flow]?.place?.state?.acronym?.message ||
      '') as string;

    return errorCity || errorState || errorStateAcronym || '';
  }

  function setCity(cityParam: SuggestionInterface) {
    const { name: city, stateCode, description } = cityParam;

    setValue(nameFieldCity, city);
    setValue(nameFieldAcronymName, stateCode || '');
    setValue(nameFieldStateName, stateCode ? StateMap[stateCode] : '');
    setValueCityState(description);
  }

  function clearErrorCity() {
    clearErrors(nameFieldCity);
    clearErrors(nameFieldStateName);
    clearErrors(nameFieldAcronymName);
  }

  function clearCity() {
    setCity({ name: '', id: '', description: '' });
  }

  function handleSetCity(cityParam: SuggestionInterface) {
    clearCity();
    clearErrorCity();
    setCity(cityParam);
  }

  function fillCity(cityParam?: JourneyMonitoringAddressDataInterface) {
    if (cityParam?.description) {
      setValue(nameFieldCity, cityParam.city.name);
      setValue(nameFieldStateName, StateMap[cityParam.state.acronym]);
      setValue(nameFieldAcronymName, cityParam.state.acronym);
      setValueCityState(cityParam.description);
    }
  }

  if (flow === 'delivery') {
    labelCityField = 'Cidade de entrega*';
  }

  useEffect(() => {
    if (fieldCityValue && fieldStateAcronyValue) {
      setValueCityState(`${fieldCityValue}, ${fieldStateAcronyValue}`);
      return;
    }

    setValueCityState('');
  }, [fieldCityValue]);

  useEffect(() => {
    if (flow === 'delivery') {
      fillCity(currentDelivery);
    }

    if (flow === 'pickup') {
      fillCity(currentPickup);
    }
  }, []);

  return (
    <Controller
      name={nameFieldCity}
      control={control}
      render={({ field: { onBlur } }) => (
        <Styled.Wrapper>
          <AutoComplete
            emptyMessage="Nenhum resultado encontrado. Tente outro endereço."
            error={getMessageCityError()}
            label={labelCityField}
            leftIcon={<FuelIcon size="small" name="pin" />}
            name={nameFieldCity}
            onSelectSuggestion={(suggestion) => handleSetCity(suggestion)}
            onSuggestionsQuery={useCitiesSuggestions}
            onBlur={onBlur}
            onClearSelectedSuggestion={() => clearCity()}
            placeholder="Informe a cidade"
            defaultValue={valueCityState}
          />
        </Styled.Wrapper>
      )}
      defaultValue={valueCityState}
    />
  );
}

export default FieldCity;
